import OmSpinner from "@/components/OmSpinner";
import { T } from "@/components/T";
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import {
  fetchEbook, fetchMyEbooks, saveMyEbook, unsaveMyEbook, setEbookProgress,
  getEbookMarks, addEbookMark, delEbookMark, addEbookNote, editEbookNote, delEbookNote,
} from "@/lib/api";
import { normalizeSrc, absMedia, PIC } from "@/lib/images";
import Seo from "@/components/Seo";
import RichText from "@/components/RichText";
import PdfReader from "@/components/PdfReader";
import { bookLd, breadcrumbLd, graph } from "@/lib/jsonld";
import { ChevronRight, Download, Bookmark, BookmarkCheck, Lock, FileText, Loader2, X, NotebookPen, Trash2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function EbookDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { pick } = useLang();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savBusy, setSavBusy] = useState(false);
  const [savedPage, setSavedPage] = useState(0);
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [jumpTo, setJumpTo] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const progressTimer = useRef(null);

  const deepPage = parseInt(sp.get("page") || "0", 10) || 0;

  useEffect(() => {
    setB(null); setNotFound(false);
    fetchEbook(slug).then(setB).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  useEffect(() => {
    if (user) {
      fetchMyEbooks().then((d) => setSaved((d.slugs || []).includes(slug))).catch(() => {});
      getEbookMarks(slug).then((d) => {
        setBookmarks(d.bookmarks || []); setNotes(d.notes || []);
        setSavedPage(deepPage || d.page || 0);
      }).catch(() => {});
    } else { setSaved(false); setSavedPage(deepPage || 0); setBookmarks([]); setNotes([]); }
  }, [user, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const loggedIn = !!user;

  // Auto-save reading position (debounced) so the book always reopens where left off.
  const onPageChange = (p) => {
    setCurrentPage(p);
    if (!loggedIn) return;
    clearTimeout(progressTimer.current);
    progressTimer.current = setTimeout(() => { setEbookProgress(slug, p).catch(() => {}); }, 1200);
  };

  const bookmarkedPages = bookmarks.map((m) => m.page);

  const togglePageBookmark = async (page) => {
    const existing = bookmarks.find((m) => m.page === page);
    try {
      if (existing) { const d = await delEbookMark(slug, existing.id); setBookmarks(d.bookmarks || []); toast(pick(`पृष्ठ ${page} का बुकमार्क हटाया`, `Removed bookmark on page ${page}`)); }
      else { const label = window.prompt(pick("इस बुकमार्क का नाम (वैकल्पिक):", "Name this bookmark (optional):"), pick(`पृष्ठ ${page}`, `Page ${page}`)); if (label === null) return; const d = await addEbookMark(slug, page, label); setBookmarks(d.bookmarks || []); toast(pick(`पृष्ठ ${page} बुकमार्क किया`, `Bookmarked page ${page}`)); }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const removeBookmark = async (id) => { try { const d = await delEbookMark(slug, id); setBookmarks(d.bookmarks || []); } catch (e) { toast.error(e.message); } };

  const saveNote = async () => {
    if (!noteDraft.trim()) return;
    try { const d = await addEbookNote(slug, currentPage, noteDraft.trim()); setNotes(d.notes || []); setNoteDraft(""); toast(pick(`पृष्ठ ${currentPage} पर टिप्पणी जोड़ी`, `Note added on page ${currentPage}`)); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try { const d = await editEbookNote(slug, id, editText.trim()); setNotes(d.notes || []); setEditingId(null); setEditText(""); }
    catch (e) { toast.error(e.message); }
  };
  const removeNote = async (id) => { try { const d = await delEbookNote(slug, id); setNotes(d.notes || []); } catch (e) { toast.error(e.message); } };

  const toggleSave = async () => {
    if (!loggedIn) return navigate("/login");
    setSavBusy(true);
    try {
      if (saved) { await unsaveMyEbook(slug); setSaved(false); toast(pick("संग्रह से हटाया गया", "Removed from your library")); }
      else { await saveMyEbook(slug); setSaved(true); toast(pick("संग्रह में सहेजा गया", "Saved to your library")); }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSavBusy(false); }
  };

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>eBook not found.</T></div>;
  if (!b) return <OmSpinner />;

  const pdf = normalizeSrc(b.pdf_url);
  const title = pick(b.title_hi, b.title) || b.title;

  return (
    <article className="pb-24" data-testid="ebook-detail">
      <Seo
        path={`/books/${b.slug}`} title={b.title} description={b.description} image={b.cover_url} type="book"
        jsonLd={graph(
          bookLd({ title: b.title, description: b.description, image: b.cover_url, path: `/books/${b.slug}`, author: b.author, year: b.year, language: b.language }),
          breadcrumbLd([{ name: "Dharmartha", path: "/" }, { name: "eBooks", path: "/books" }, { name: b.title, path: `/books/${b.slug}` }])
        )}
      />

      <div className="mx-auto max-w-5xl px-5 md:px-8">
        <nav className="flex items-center gap-1.5 py-6 text-xs text-ink-3">
          <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/books" className="hover:text-saffron">{pick("ग्रंथालय", "eBooks")}</Link>
        </nav>

        <div className="grid gap-8 md:grid-cols-[220px_1fr]">
          <div className="mx-auto w-full max-w-[220px]">
            <div className="overflow-hidden rounded-lg border border-line bg-parchment shadow-md">
              {b.cover_url ? (
                <img src={normalizeSrc(b.cover_url) || PIC.manuscript} alt={title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex aspect-[3/4] items-center justify-center"><FileText className="h-12 w-12 text-gold/50" /></div>
              )}
            </div>
          </div>
          <div>
            {b.category && <span className="overline-label text-saffron">{b.category}</span>}
            <h1 className="mt-1 font-serif-latin text-3xl text-ink md:text-4xl">{title}</h1>
            {(b.author || b.year) && <p className="mt-2 text-sm text-ink-2">{[b.author, b.year].filter(Boolean).join(" · ")}</p>}
            {b.description && <div className="mt-4 text-ink-2"><RichText value={pick(b.description_hi, b.description) || b.description} /></div>}
            {b.source && <p className="mt-3 text-xs italic text-ink-3">{pick("स्रोत", "Source")}: {b.source}</p>}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button onClick={toggleSave} disabled={savBusy} data-testid="ebook-save-btn" className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
                {savBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <BookmarkCheck className="h-4 w-4 text-saffron" /> : <Bookmark className="h-4 w-4" />}
                {saved ? pick("सहेजा गया", "Saved") : pick("संग्रह में सहेजें", "Save to library")}
              </button>
              {loggedIn && pdf ? (
                <a href={absMedia(b.pdf_url)} download data-testid="ebook-download-btn" className="inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5">
                  <Download className="h-4 w-4" /> {pick("डाउनलोड करें", "Download PDF")}
                </a>
              ) : (
                <Link to="/login" data-testid="ebook-download-locked" className="inline-flex items-center gap-2 rounded-full border border-dashed border-line px-4 py-2 text-sm text-ink-3 transition-colors hover:border-saffron hover:text-saffron">
                  <Lock className="h-4 w-4" /> {pick("डाउनलोड हेतु साइन इन करें", "Sign in to download")}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Reader */}
        <div className="mt-10 border-t border-line pt-8">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif-latin text-xl text-ink">{pick("पूर्वावलोकन", "Read")}</h2>
            {loggedIn && savedPage > 1 && (
              <span className="rounded-full bg-[var(--saffron)]/12 px-3 py-1 text-xs text-saffron" data-testid="ebook-resume-note">
                {pick(`पृष्ठ ${savedPage} से जारी`, `Resuming page ${savedPage}`)}
              </span>
            )}
          </div>
          {pdf ? (
            <div className="overflow-hidden rounded-lg border border-line bg-[var(--surface-alt)] p-3" data-testid="ebook-viewer-wrap">
              <PdfReader
                file={absMedia(b.pdf_url)}
                initialPage={savedPage}
                jumpTo={jumpTo}
                onPageChange={onPageChange}
                bookmarkedPages={bookmarkedPages}
                onToggleBookmark={loggedIn ? togglePageBookmark : null}
                labels={{
                  loading: pick("लोड हो रहा है…", "Loading…"),
                  error: pick("यह दस्तावेज़ लोड नहीं हो सका।", "This document could not be loaded."),
                  bookmark: pick("बुकमार्क", "Bookmark"),
                  bookmarked: pick("सहेजा गया", "Bookmarked"),
                }}
              />
            </div>
          ) : (
            <p className="text-ink-3">{pick("इस ग्रंथ का PDF अभी उपलब्ध नहीं है।", "The PDF for this title is not available yet.")}</p>
          )}
          {!loggedIn && (
            <p className="mt-3 text-center text-sm text-ink-3">
              {pick("आप इसे यहाँ पढ़ सकते हैं। सहेजने, बुकमार्क एवं टिप्पणी हेतु ", "You can read this here. To save, bookmark and take notes, ")}
              <Link to="/login" className="text-saffron underline">{pick("साइन इन करें", "sign in")}</Link>.
            </p>
          )}
        </div>

        {/* Bookmarks + Notes (signed-in) */}
        {loggedIn && pdf && (
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {/* Bookmarks list */}
            <div className="border border-line bg-surface p-5" data-testid="ebook-bookmarks-panel">
              <div className="mb-3 flex items-center gap-2"><Bookmark className="h-4 w-4 text-saffron" /><h3 className="font-serif-latin text-lg text-ink">{pick("बुकमार्क", "Bookmarks")}</h3></div>
              {bookmarks.length === 0 ? (
                <p className="text-sm text-ink-3">{pick("अभी कोई बुकमार्क नहीं। पाठक में किसी पृष्ठ को बुकमार्क करें।", "No bookmarks yet. Use the bookmark button in the reader to save a page.")}</p>
              ) : (
                <ul className="space-y-2">
                  {bookmarks.map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-2 border border-line/70 px-3 py-2" data-testid={`ebook-bookmark-${m.page}`}>
                      <button onClick={() => setJumpTo({ page: m.page, n: Date.now() })} className="flex flex-1 items-center gap-2 text-left text-sm text-ink hover:text-saffron" data-testid={`ebook-bookmark-open-${m.page}`}>
                        <span className="rounded-full bg-parchment px-2 py-0.5 text-xs tabular-nums text-ink-2"><T>p.</T>{m.page}</span>
                        <span className="line-clamp-1">{m.label}</span>
                      </button>
                      <button onClick={() => removeBookmark(m.id)} title={pick("हटाएँ", "Remove")} data-testid={`ebook-bookmark-remove-${m.page}`} className="shrink-0 text-ink-3 hover:text-red-600"><X className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Notes */}
            <div className="border border-line bg-surface p-5" data-testid="ebook-notes-panel">
              <div className="mb-3 flex items-center gap-2"><NotebookPen className="h-4 w-4 text-saffron" /><h3 className="font-serif-latin text-lg text-ink">{pick("टिप्पणियाँ", "Notes")}</h3></div>
              <div className="mb-4">
                <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={2} data-testid="ebook-note-input"
                  placeholder={pick(`पृष्ठ ${currentPage} के लिए टिप्पणी लिखें…`, `Write a note for page ${currentPage}…`)}
                  className="w-full resize-y border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
                <button onClick={saveNote} disabled={!noteDraft.trim()} data-testid="ebook-note-save" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
                  <Plus className="h-3.5 w-3.5" /> {pick(`पृष्ठ ${currentPage} पर जोड़ें`, `Add to page ${currentPage}`)}
                </button>
              </div>
              {notes.length === 0 ? (
                <p className="text-sm text-ink-3">{pick("अभी कोई टिप्पणी नहीं।", "No notes yet.")}</p>
              ) : (
                <ul className="space-y-2">
                  {notes.map((n) => (
                    <li key={n.id} className="border border-line/70 px-3 py-2" data-testid={`ebook-note-${n.id}`}>
                      <div className="mb-1 flex items-center justify-between">
                        <button onClick={() => setJumpTo({ page: n.page, n: Date.now() })} className="rounded-full bg-parchment px-2 py-0.5 text-xs tabular-nums text-ink-2 hover:text-saffron"><T>p.</T>{n.page}</button>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setEditingId(n.id); setEditText(n.text); }} className="text-ink-3 hover:text-saffron"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => removeNote(n.id)} data-testid={`ebook-note-remove-${n.id}`} className="text-ink-3 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                      {editingId === n.id ? (
                        <div>
                          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="w-full resize-y border border-line bg-parchment px-2 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none" />
                          <div className="mt-1.5 flex gap-2">
                            <button onClick={() => saveEdit(n.id)} className="rounded-full bg-[var(--saffron)] px-3 py-1 text-xs text-white">{pick("सहेजें", "Save")}</button>
                            <button onClick={() => { setEditingId(null); setEditText(""); }} className="rounded-full border border-line px-3 py-1 text-xs text-ink-2">{pick("रद्द", "Cancel")}</button>
                          </div>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap text-sm text-ink-2">{n.text}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
